export interface ILastConnectionLike {
    isConnected?: boolean;
    redirectUrl?: string;
}

/**
 * Returns the URL to redirect to when the resolved last-used org is an SSO
 * connection on a DIFFERENT subdomain than `currentHost`; otherwise null.
 * The host check prevents an infinite reload loop on the connected subdomain.
 */
export const getCrossSubdomainRedirect = (
    lastConnection: ILastConnectionLike | null | undefined,
    currentHost: string
): string | null => {
    if (!lastConnection || !lastConnection.isConnected || !lastConnection.redirectUrl) {
        return null;
    }
    try {
        return new URL(lastConnection.redirectUrl).host !== currentHost
            ? lastConnection.redirectUrl
            : null;
    } catch {
        return null;
    }
};

/** Side-effecting redirect, wrapped so the saga can yield it as an assertable `call` effect. */
export const redirectTo = (url: string): void => {
    window.location.href = url;
};

/**
 * True when redirects to connected-org SSO sites are suppressed. Suppression is a
 * local-debugging convenience ONLY: it requires the Vite dev server (import.meta.env.DEV),
 * and production builds bake DEV to false, so no env var or runtime state can ever
 * disable SSO redirects in production — do not weaken this gate to a runtime flag.
 * `npm run start` suppresses redirects by default; `npm run start:sso` sets
 * VITE_SSO_REDIRECT=true to exercise the real redirect flow locally. Vitest
 * (MODE "test") is excluded so the suite always tests production behavior.
 */
export const isSsoRedirectSuppressed = (env: ImportMetaEnv = import.meta.env): boolean =>
    Boolean(env.DEV) && env.MODE !== "test" && env.VITE_SSO_REDIRECT !== "true";
